<template>
  <div class="data-form">

    <el-form
      ref="form"
      :disabled="disabled"
      :model="formModel"
      :rules="cachedRules"
      :label-position="labelPosition"
      :label-width="labelWidth"
      :size="size"
      @keyup.native.enter="validate">

      <el-form-item
        v-for="props in visibleFormFields"
        :key="props.key"
        :label="props.type !== 'hidden' ? $t('label.' + (props.label || props.key)) : ''"
        :prop="props.key">

        <!-- ================= -->
        <!-- Radion group -->
        <!-- ================= -->

        <el-radio-group
          v-if="props.type === 'radio'"
          v-model="formModel[props.key]"
          :disabled="props.disabled"
          @change="change">

          <el-radio
            border
            v-for="item in props.options"
            :key="item.value"
            :label="item.value">{{ $t(item.label) }}</el-radio>

        </el-radio-group>

        <!-- ================= -->
        <!-- Select -->
        <!-- ================= -->

        <el-select
          v-else-if="props.type === 'select'"
          v-model="formModel[props.key]"
          filterable
          :remote="!(!remoteMethod(props.key))"
          :multiple="props.multiple"
          :remote-method="remoteMethod(props.key)"
          :placeholder="props.placeholder && $t('placeholder.' + props.key)"
          :disabled="props.disabled"
          :clearable="true"
          @change="(...args) => {
            change(...args);
            props.change && props.change(...args);
          }">

          <el-option
            v-for="item in props.options"
            :key="item.value"
            :label="$t(item.label)"
            :value="item.value" />

        </el-select>

        <!-- ================= -->
        <!-- Checkbox -->
        <!-- ================= -->

        <el-checkbox
          v-else-if="props.type === 'checkbox'"
          v-model="formModel[props.key]"
          :disabled="props.disabled"
          @change="change"/>


        <!-- ================= -->
        <!-- Switch -->
        <!-- ================= -->

        <el-switch
          v-else-if="props.type === 'switch'"
          v-model="formModel[props.key]"
          :disabled="props.disabled"
          @change="change"/>

        <!-- ================= -->
        <!-- Date/DateTimepicker -->
        <!-- ================= -->

        <el-date-picker
          v-else-if="props.type === 'datetime' || props.type === 'date'"
          v-model="formModel[props.key]"
          :disabled="props.disabled"
          :format="props.format"
          :type="props.type"
          :placeholder="props.placeholder && $t('placeholder.' + props.key)"
          time-arrow-control
          @change="change"/>

        <!-- ================= -->
        <!-- Time picker -->
        <!-- ================= -->

        <el-time-picker
          v-else-if="props.type === 'time'"
          v-model="formModel[props.key]"
          :disabled="props.disabled"
          is-range
          :format="props.format"
          :picker-options="props.options"
          :placeholder="props.placeholder && $t('placeholder.' + props.key)"
          @change="change"/>

        <!-- ================= -->
        <!-- Number -->
        <!-- ================= -->

        <el-input-number
          v-else-if="props.type === 'number'"
          v-model="formModel[props.key]"
          :max="props.max"
          :min="props.min"
          :step="props.step"
          :disabled="props.disabled"
          @change="changeNum(props.key)"
        />

        <!-- ================= -->
        <!-- Default input -->
        <!-- ================= -->

        <el-input
          v-else
          v-model="formModel[props.key]"
          :type="props.type"
          :disabled="props.disabled"
          :placeholder="props.placeholder && $t('placeholder.' + props.key)"
          @change="props.type === 'number' ? changeNum(props.key) : change()"/>

      </el-form-item>

      <div :style="{ marginLeft: hideButtons ? labelWidth : 0 }">
        <slot></slot>
      </div>

      <el-form-item v-if="!hideButtons">

        <template v-if="!buttons">
          <el-button
            type="primary"
            size="large"
            @click="validate()"
            :loading="isLoading">{{ $t('button.' + submitLabel ) }}</el-button>
        </template>

        <template v-else>
          <el-button
            v-for="b in buttons"
            :type="b.type || 'primary'"
            :size="b.size || 'large'"
            :icon="b.icon && 'el-icon-' + b.icon"
            @click="validate(b)"
            :loading="isLoading">{{ $t('button.' + b.label ) }}</el-button>
        </template>

        <el-button
          v-if="cancel"
          type="default"
          size="large"
          :plain="true"
          @click="onCancel">{{ $t('button.cancel') }}</el-button>

      </el-form-item>

    </el-form>

  </div>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'data-form',
  props: {
    entity: {
      type: Object,
      required: true
    },
    fields: {
      type: Object,
      required: true
    },
    model: {
      type: String
    },
    size: { // large / medium / small / mini
      type: String,
      default: 'large'
    },
    cancel: {
      default: true
    },
    cancelAction: {
      type: Function
    },
    labelPosition: {
      default: 'right'
    },
    submitLabel: {
      default: 'submit'
    },
    beforeSubmit: {
      type: Function,
      default: () => {}
    },
    afterSubmit: {
      type: Function,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    },
    hideButtons: {
      type: Boolean,
      default: false
    },
    labelWidth: {
      default: '150px'
    },
    buttons: {
      type: Array
    },
    ignoreEntityChanges: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      formModel: _.cloneDeep(this.entity),
      formFields: this.fields,
      isLoading: false,
      cachedRules: null,
      changeCount: 0
    }
  },
  computed: {
    rules () {
      // More: https://github.com/yiminghe/async-validator
      const rules = {}

      Object.keys(this.formFields).forEach((key) => {
        let entry = this.formFields[key]

        if (!entry.rules) return

        rules[key] = entry.rules

        // Set default trigger to blur
        rules[key].forEach((rule) => { rule.trigger = rule.trigger !== 'change' ? 'blur' : 'change' })
      })

      return rules
    },
    visibleFormFields () {
      return Object.entries(this.formFields)
        .map(([key, value]) => ({
          ...value,
          key
        }))
        .filter(ff => !ff.hidden)
    }
  },
  watch: {
    entity (val) {
      if (this.changeCount > 1 && this.ignoreEntityChanges) {
        console.debug('ignoring dataform update')

        return
      }

      this.formModel = _.cloneDeep(val)
      this.changeCount++
    },
    fields: {
      handler (val) {
        this.formFields = val
      },
      deep: true
    },
    loading (val) {
      this.isLoading = val
    },
    rules: {
      handler (val, last) {
        // Rules are always recomputed and this would start
        // a validation that we dont need at the page load
        // So we must not let the rules reassign while they would be the same
        if (JSON.stringify(val) !== JSON.stringify(last)) {
          this.cachedRules = val
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    validate (button) {
      this.$refs.form.validate((valid) => {
        if (!valid) return

        if (this.model && (!button || button.submit)) {
          this.submit(button)
        }
        else {
          this.$emit('submit', this.formModel)
        }
      })
    },
    async submit (button) {
      let formModel = Object.assign({}, this.formModel)

      if (button && button.params) {
        formModel = Object.assign({}, formModel, button.params)
      }

      this.beforeSubmit(formModel)

      this.isLoading = true

      try {
        const action = this.formModel.new ? 'create' : 'edit'

        await this.$store.dispatch(`${this.model}/${action}`, formModel)

        this.afterSubmit(formModel)
      }
      catch (err) {
        console.error(err)
      }
      finally {
        this.isLoading = false
      }
    },
    remoteMethod (field) {
      const filterHandler = this.formFields[field].filter

      if (!filterHandler) { return }

      return (query) => {
        this.formFields[field].options = filterHandler(query)
      }
    },
    onCancel () {
      if (this.cancelAction) {
        this.cancelAction()
      }
      else {
        this.$router.back()
      }
    },
    changeNum (field) {
      this.formModel[field] = parseInt(this.formModel[field])

      this.change()
    },
    change () {
      this.$emit('change', this.formModel)
    }
  }
}
</script>

<style lang="scss">
.data-form {
  background: white;
  padding: 20px;
  border: 1px solid #ebeef5;

  .el-select {
    width: 100%;
  }
}
</style>


// WEBPACK FOOTER //
// DataForm.vue?d626a30e